<template>
  <v-form ref="form" fast-fail @submit.prevent>
    <Modal
      v-if="props.isOpened"
      id="custom-metric-modal"
      :is-open="props.isOpened"
      :height="calculateHeight()"
      :width="calculateWidth()"
      :title="
        props.customMetric
          ? labels.CUSTOM_METRIC.DIALOG_TITLE_EDIT
          : labels.CUSTOM_METRIC.DIALOG_TITLE_NEW
      "
      :title-class="'text-center'"
      :header-color="CONSTANTS.DIALOGS_HEADER_color"
      :card-actions-enabled="true"
      :disable-close="!props.isViewMode"
      @close-dialog="cancel"
    >
      <template #body>
        <analyze-name-description
          :input-name="currentCustomMetric ? currentCustomMetric.name : ''"
          :input-name-id="`name-field-custom-metric-${customMetric?.id}`"
          :input-name-label="labels.CUSTOM_METRIC.CUSTOM_METRICS"
          :input-name-placeholder="labels.CUSTOM_METRIC.DEFAULT_METRIC_NAME"
          :input-description="currentCustomMetric ? currentCustomMetric.description : ''"
          :input-description-id="`description-field-custom-metric-${customMetric?.id}`"
          :input-description-label="labels.CUSTOM_METRIC.CUSTOM_METRICS_DESCRIPTION"
          :input-description-placeholder="labels.CUSTOM_METRIC.DEFAULT_METRIC_DESCRIPTION"
          :is-view-mode="props.isViewMode"
          @description-input="applyDescription"
          @name-input="applyMetricName"
        />
        <div
          class="d-flex flex-wrap bg-surface-variant"
          :class="{ 'flex-row-reverse': isReversed }"
        >
          <div class="flex-1-0 ma-2 pa-2 toolbox-container">
            <custom-metric-tool-box
              :on-toggle-button-click="toggleLayout"
              :toolbox="toolbox"
            ></custom-metric-tool-box>
          </div>
          <div class="ma-2 pa-2 table-container">
            <custom-metric-table
              :disabled="props.isViewMode"
              :query="currentCustomMetric?.query"
              :current-context-uuids="[]"
              @open-filter-menu="openFilterMenu"
            ></custom-metric-table>
          </div>
        </div>
      </template>

      <template #btn-group>
        <v-row v-if="!props.isViewMode" class="justify-center my-5">
          <v-btn class="secondary-btn action-btn" variant="flat" @click="cancel">
            {{ labels.CUSTOM_METRIC.CANCEL }}
          </v-btn>
          <v-btn
            type="submit"
            class="primary-btn action-btn ml-5"
            variant="flat"
            @click="saveCustomMetric"
          >
            {{ labels.CUSTOM_METRIC.SAVE }}
          </v-btn>
        </v-row>

        <v-row v-else class="justify-center my-5">
          <v-btn class="primary-btn" variant="flat" @click="editCustomMetric">
            {{ labels.CUSTOM_METRIC.EDIT }}
          </v-btn>
        </v-row>
      </template>
    </Modal>
  </v-form>

  <custom-metric-filter-modal
    :is-opened="openCustomMetricFilterModal"
    :query="query"
    :tools="[]"
    :is-view-mode="isViewMode"
    @close-dialog="
      (filter) => {
        if (query) query.filter = filter
        openCustomMetricFilterModal = false
      }
    "
  />
</template>

<script setup lang="ts">
  import CONSTANTS from '@/constants/Config.constants'
  import labels from '@/translations/labels.json'
  import Modal from '@/components/Modal/Modal.vue'
  import { ref, watch } from 'vue'
  import { useProjectStore } from '@/stores/projectStore'
  import { storeToRefs } from 'pinia'
  import { VForm } from 'vuetify/lib/components/index.mjs'
  import { CustomMetric, Query, Toolbox } from '@/models/custom-metric.model'
  import { useCustomMetricStore } from '@/stores/customMetricStore'
  import CustomMetricToolBox from '@/components/CustomMetrics/CustomMetricToolBox.vue'
  import CustomMetricTable from '@/components/CustomMetrics/CustomMetricTable.vue'
  import AnalyzeNameDescription from '@/components/AnalyzeNameDescription/AnalyzeNameDescription.vue'
  import { EventBus } from '@/event-bus'
  import CustomMetricFilterModal from '@/components/CustomMetrics/CustomMetricFilterModal.vue'
  interface Props {
    isOpened: boolean
    customMetric: CustomMetric | undefined
    isViewMode: boolean
  }

  const toolbox = ref<Toolbox>({})

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (event: 'closeDialog'): void
    (event: 'disableViewMode', value: boolean): void
  }>()

  const customMetricStore = useCustomMetricStore()
  const projectStore = useProjectStore()

  const { currentProject } = storeToRefs(projectStore)

  const customMetricName = ref<string>(props.customMetric?.name ?? '')
  const customMetricDescription = ref<string>(props.customMetric?.description ?? '')

  const currentCustomMetric = ref<CustomMetric | undefined>(props.customMetric)

  const form = ref<VForm | null>(null)

  const query = ref<Query | undefined>(props.customMetric?.query)

  const isReversed = ref(false)

  const openCustomMetricFilterModal = ref(false)

  EventBus.on('updateCustomMetricQueryInModal', handleUpdateQuery)

  watch(
    () => props.isOpened,
    () => {
      if (props.isOpened) {
        customMetricName.value = props.customMetric?.name ?? ''
        customMetricDescription.value = props.customMetric?.description ?? ''
        currentCustomMetric.value = props.customMetric
        query.value = props.customMetric?.query
      }
    },
  )

  function applyMetricName(name: string) {
    customMetricName.value = name
  }

  function applyDescription(description: string) {
    customMetricDescription.value = description
  }

  function handleUpdateQuery(updatedQuery: Query) {
    query.value = updatedQuery
  }

  function calculateWidth(): string {
    const value = (window.innerWidth * 0.9).toString() + 'px'
    return value
  }

  function calculateHeight(): string {
    const value = (window.innerHeight * 0.9).toString() + 'px'
    return value
  }

  async function saveCustomMetric(): Promise<void> {
    if (!currentProject?.value || !form.value || customMetricName.value == '') return

    if (props.customMetric && query.value) {
      const filteredSelect =
        query.value?.select.filter((item) => item.field.type !== 'toolbox.assist') || []
      const customMetricToUpdate: CustomMetric = {
        name: customMetricName.value,
        id: props.customMetric.id,
        description: customMetricDescription.value || '⠀⠀⠀',
        query: {
          ...query.value,
          select: filteredSelect,
        },
      }

      await customMetricStore.updateCustomMetric(currentProject.value.id, customMetricToUpdate)
    } else {
      if (query.value) {
        const filteredSelect =
          query.value?.select.filter((item) => item.field.type !== 'toolbox.assist') || []
        const newCustomMetric: Omit<CustomMetric, 'id'> = {
          name: customMetricName.value,
          description: customMetricDescription.value || '⠀⠀⠀',
          query: {
            ...query.value,
            select: filteredSelect,
          },
        }
        await customMetricStore.createNewCustomMetric(currentProject.value.id, newCustomMetric)
      }
    }

    emit('closeDialog')
  }

  function editCustomMetric(): void {
    emit('disableViewMode', false)
  }

  function cancel(): void {
    emit('closeDialog')
  }

  function toggleLayout() {
    isReversed.value = !isReversed.value
  }

  function openFilterMenu() {
    openCustomMetricFilterModal.value = true
  }
</script>

<style scoped>
  .flexible-container {
    display: flex;
    width: 100%;
    overflow-x: hidden;
  }

  .toolbox-container {
    flex: 0 0 250px;
    max-width: 250px;
    border: 2px solid #ccc;
    padding: 10px;
    box-sizing: border-box;
  }

  .table-container {
    flex: 1;
    overflow-x: auto;
  }

  .inline-header-container {
    display: inline-flex;
    align-items: center;
  }

  .edit-icon {
    margin-left: 10px;
  }

  .custom-metric-name-input,
  .custom-metric-description-input {
    max-width: 550px;
    margin-bottom: 20px;
  }

  .primary-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
  }

  .secondary-btn {
    background-color: rgb(var(--v-theme-itemis-hidden-grey));
    color: rgb(var(--v-theme-surface));
  }

  #custom-metric-modal .v-card-title {
    justify-content: center;
    display: flex;
  }

  .action-btn {
    width: 180px;
  }
</style>
